/**
 * src/core/storage/graphPageCache.ts
 *
 * F 452 — THE BROWSER'S COPY OF THE PAGES IT HAS ALREADY READ.
 *
 * WHY THIS EXISTS. The app reads a database's items one page at a time
 * (F 486's paged route, F 497's reader). That is what makes the app fast
 * online — and it is what makes it USELESS offline: a page that was never
 * fetched cannot be shown, so pulling the plug means the operator loses the
 * page they were reading. The dispatch asks for the app to "work online and
 * offline", and offline means the pages you have already seen are still
 * there.
 *
 * WHERE IT LIVES, AND WHY NOT localStorage. A page is 50 items, and the HQ
 * Backup's items are ~1 KB each — so a page is ~50 KB and the full set is
 * ~16.9 MB. localStorage's quota is ~5 MB and it is already close to full
 * with the workspace mirror (F 451). IndexedDB has no such ceiling, and the
 * app already ships an IndexedDB adapter for its own storage.
 *
 * WHAT IT IS NOT. It is not a mirror of the graph and not a source of
 * truth: it holds PAGES, keyed by the exact read that produced them, and it
 * is only ever consulted AFTER a live read has failed. A cached page is
 * labelled as cached, and the surface that shows it says so.
 *
 * IT DEGRADES, IT DOES NOT THROW. A browser with IndexedDB disabled (a
 * private window, a locked-down profile, a test environment) gets a cache
 * that misses instead of an exception, because a cache is an optimisation
 * and must never be able to break the read that precedes it.
 */

const DB_NAME = 'freshcards-graph-pages';
const STORE_NAME = 'pages';
const DB_VERSION = 1;

/** One page, exactly as the service returned it. */
export interface GraphPage {
  /** The prefix the page was read for. */
  prefix: string;
  /** The offset the page was read at. */
  offset: number;
  /** The page size the page was read with. */
  limit: number;
  /** The key/value pairs the service returned for this page. */
  values: Record<string, string>;
  /**
   * The service's count of the whole prefix, and the offset of the page after
   * this one. Cached WITH the page, because a page read from the cache that
   * cannot say "1-50 of 16,783" is a worse answer than the live one.
   */
  total?: number;
  nextOffset?: number;
  /** When this page was written, so a caller can tell a stale page from a fresh one. */
  cachedAt: number;
}

/** The cache key. The read that produced the page IS its identity. */
function pageKey(prefix: string, offset: number, limit: number): string {
  return `${prefix}|${offset}|${limit}`;
}

/** Whether this environment has IndexedDB at all. */
function hasIndexedDb(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (!hasIndexedDb()) return Promise.resolve(null);
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
  return dbPromise;
}

/**
 * Write a page the app has just read.
 *
 * Returns whether it was stored. A failure is not an error: the page is
 * still in memory and still on its way to the screen.
 */
export async function writeGraphPage(
  prefix: string,
  offset: number,
  limit: number,
  values: Record<string, string>,
  meta?: { total?: number; nextOffset?: number },
): Promise<boolean> {
  try {
    const db = await openDb();
    if (!db) return false;
    const page: GraphPage = {
      prefix,
      offset,
      limit,
      values,
      total: meta?.total,
      nextOffset: meta?.nextOffset,
      cachedAt: Date.now(),
    };
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(page, pageKey(prefix, offset, limit));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Read a page back.
 *
 * A miss is `null`, never an exception: the caller is deciding whether to
 * fall back, and a thrown error would turn a cache miss into a failed read.
 */
export async function readGraphPage(
  prefix: string,
  offset: number,
  limit: number,
): Promise<GraphPage | null> {
  try {
    const db = await openDb();
    if (!db) return null;
    const page = await new Promise<GraphPage | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(pageKey(prefix, offset, limit));
      req.onsuccess = () => resolve(req.result as GraphPage | undefined);
      req.onerror = () => reject(req.error);
    });
    if (!page || typeof page !== 'object') return null;
    if (!page.values || typeof page.values !== 'object') return null;
    return page;
  } catch {
    return null;
  }
}

/** Forget every cached page. Used by the reset/logout flows. */
export async function clearGraphPages(): Promise<void> {
  try {
    const db = await openDb();
    if (!db) return;
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } catch {
    // A cache that cannot be cleared is not worth failing a logout over.
  }
}
